<template>
  <Dialog
    :visible="visible"
    modal
    :header="'Personalize Content'"
    class="max-w-4xl"
    @update:visible="$emit('update:visible', $event)"
  >
    <div class="space-y-6">
      <!-- Question Selection -->
      <div v-if="!selectedSegmentGroupId">
        <FormGroup label="Select a question to personalize for...">
          <template
            v-if="segmentationErrors"
            #description
          >
            <span class="text-sm text-red-800">
              {{ segmentationErrors }}
            </span>
          </template>
        </FormGroup>

        <div class="mt-4">
          <GridList
            class="mt-8"
            name="questions-for-personalization"
            :items="filteredItems"
            :show-list="false"
            :big-filter="true"
            :columns="[]"
            :column-count="2"
            @filter="filterQuery = $event"
            @row-select="selectQuestion($event.data.id)"
          >
            <template #grid-item="{ item }">
              <div
                class="bg-surface-50/30 ring-surface-200/70 hover:ring-surface-900/50 dark:bg-surface-950/20 dark:ring-surface-200/20 dark:hover:ring-surface-200/50 cursor-pointer rounded p-6 shadow ring ring-1 transition-all duration-100"
                @click="selectQuestion(item.id)"
              >
                <div class="space-y-2">
                  <div
                    class="text-surface-800 text-sm font-semibold"
                    v-html="item.name"
                  />
                  <div
                    class="text-lg"
                    v-html="item.question"
                  />
                  <div
                    class="text-sm opacity-70"
                    v-html="item.options.join(' / ')"
                  />
                </div>
              </div>
            </template>
          </GridList>
        </div>
      </div>

      <!-- Segment Selection -->
      <div v-else>
        <div class="mb-4 flex items-center justify-between">
          <h3 class="text-lg font-semibold">
            Select segments to personalize for
          </h3>
          <Button
            icon="pi pi-arrow-left"
            label="Change Question"
            outlined
            severity="secondary"
            size="small"
            @click="changeQuestion"
          />
        </div>

        <SegmentGroupCardWithSelection
          v-if="selectedSegmentGroup"
          :segment-group="selectedSegmentGroup"
          :selected-segments="selectedSegments"
          @update:selected-segments="selectedSegments = $event"
        />
      </div>

      <hr>

      <FormGroup
        label="Is there anything specific you'd like to have us focus on when personalizing this content?"
      >
        <RightyInline
          :emit-only="true"
          :currently-generating="isGenerating"
          :allow-empty-submit="true"
          placeholder="Describe what to focus on when personalizing..."
          button-label="Generate"
          :show-response="false"
          class="mt-6"
          @submit="generate"
        />
      </FormGroup>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import SegmentGroupCardWithSelection from '@/modules/segment/components/SegmentGroupCardWithSelection.vue'
import RightyInline from '@/modules/righty/components/RightyInline.vue'
import { useRightyHeadless } from '@/modules/righty/composables/useRightyHeadless'

const props = defineProps<{
  visible: boolean
  selectedText: string
  surroundingContent: string
}>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'personalized', content: string): void
}>()

const selectedSegmentGroupId = ref<string | null>(null)
const selectedSegments = ref<string[]>([])
const segmentationErrors = ref<null | string>(null)
const filterQuery = ref('')

const segmentGroupStore = useSegmentGroupStore()
const questionStore = useQuestionStore()
const configStore = useConfigStore()
const { currentTeamPid } = storeToRefs(configStore)

// Use Righty headless for content personalization
const { personalizeContent, isGenerating } = useRightyHeadless({
  teamPid: currentTeamPid.value
})

const customFields = asyncComputed(async () => {
  return (await getIntegrationData('partner', 'custom_fields')).value
}, [])

const availableSegmentGroups = computed(() => {
  return segmentGroups.value.filter((sg) => {
    return (
      isAQuestion(sg) &&
      (segmentGroupSyncSetupCompletion(sg) === 'some_values' ||
        segmentGroupSyncSetupCompletion(sg) === 'all_values')
    )
  })
})

const segmentGroupsForSearch = computed(() => {
  return availableSegmentGroups.value.map((sg) => {
    const question = questionStore.getBySegmentGroupId(sg.id)
    return {
      id: sg.id,
      question: stripHtml(renderText(question?.text || '', 1000)),
      name: sg.name,
      options: sg.segments.map((s) => stripHtml(segmentName(s)))
    }
  })
})

const highlightMatch = (text: string, query: string) => {
  if (!query) return text
  const regex = new RegExp(`(${query})`, 'gi')
  return text.replace(regex, '<span class="found">$1</span>')
}

const filteredItems = computed(() => {
  if (!filterQuery.value) return segmentGroupsForSearch.value

  const query = filterQuery.value.toLowerCase()
  return segmentGroupsForSearch.value
    .filter((sg) => {
      const nameMatch = sg.name.toLowerCase().includes(query)
      const questionMatch = sg.question.toLowerCase().includes(query)
      const optionsMatch = sg.options.some((s) =>
        s.toLowerCase().includes(query)
      )

      return nameMatch || questionMatch || optionsMatch
    })
    .map((sg) => ({
      ...sg,
      name: highlightMatch(sg.name, query),
      question: highlightMatch(sg.question, query),
      options: sg.options.map((s) => highlightMatch(s, query))
    }))
})

const selectedSegmentGroup = computed(() => {
  return selectedSegmentGroupId.value
    ? segmentGroupStore.getById(selectedSegmentGroupId.value)
    : null
})

// Helper function to focus the search input
const focusSearchInput = async () => {
  await nextTick()
  // Try to find the search input by class and size attribute
  const searchInput = document.querySelector('input[size="large"][placeholder="Search"]') as HTMLInputElement
  if (searchInput) {
    searchInput.focus()
  }
}

const selectQuestion = (segmentGroupId: string) => {
  selectedSegmentGroupId.value = segmentGroupId
  const sg = segmentGroupStore.getById(segmentGroupId)
  if (sg) {
    // Select all segments by default
    selectedSegments.value = sg.segments.map((s) => s.id)
  }
}

const changeQuestion = () => {
  selectedSegmentGroupId.value = null
  selectedSegments.value = []
  filterQuery.value = '' // Reset the search filter
  focusSearchInput() // Focus the search input
}

const { segmentGroups } = storeToRefs(segmentGroupStore)

// Watch for modal visibility to focus search input
watch(
  () => props.visible,
  async (newValue) => {
    if (newValue && !selectedSegmentGroupId.value) {
      // Focus search input when opening modal without a selected question
      await focusSearchInput()
    }
  }
)

const generate = async (content: string) => {
  if (!selectedSegmentGroupId.value) {
    segmentationErrors.value = 'Please select a question'
    return
  }

  if (selectedSegments.value.length === 0) {
    segmentationErrors.value = 'Please select at least one segment'
    return
  }

  segmentationErrors.value = null

  try {
    const result = await personalizeContent(
      props.selectedText,
      props.surroundingContent,
      enrichedSelectedSegments.value,
      content || undefined // Pass instructions if provided
    )

    if (result) {
      emit('personalized', result)
      emit('update:visible', false)
    } else {
      const notificationStore = useNotificationStore()
      notificationStore.addError('Failed to generate personalized content')
    }
  } catch (error) {
    console.error('Personalization error:', error)
    const notificationStore = useNotificationStore()
    notificationStore.addError('Failed to generate personalized content')
  }
}

const enrichedSelectedSegments = computed(() => {
  if (!selectedSegmentGroup.value) return []

  const question = questionStore.getBySegmentGroupId(
    selectedSegmentGroup.value.id
  )

  let fieldName = selectedSegmentGroup.value.syncToCustomField
  const foundFieldName = customFields.value.find(
    (cf) => String(cf.id) === fieldName
  )
  if (foundFieldName) {
    // Use templatingToken for Liquid syntax (e.g., subscriber.industry), fall back to label, then ID
    fieldName = foundFieldName.templatingToken || foundFieldName.label || fieldName
  }

  // Only include the selected segments
  const filteredSegments = selectedSegmentGroup.value.segments
    .filter((s) => selectedSegments.value.includes(s.id))
    .filter((s) => autoSyncLogicDefinitionForSegment(s)?.value)
    .map((s) => ({
      id: s.id,
      fieldValue: autoSyncLogicDefinitionForSegment(s)?.value || null,
      name: segmentName(s) || ''
    }))

  return [
    {
      name: selectedSegmentGroup.value.name,
      questionText: stripHtml(renderText(question?.text || '')),
      fieldName,
      segments: filteredSegments.map(({ id, name, fieldValue }) => ({
        segmentId: id,
        name,
        fieldValue
      }))
    }
  ]
})
</script>

<style scoped>
.found {
  background-color: rgba(255, 255, 0, 0.3);
  font-weight: bold;
}
</style>

